/**
 * SMSWiser Country Dashboard
 * For Media Devoted | Created By Ciro Autiero
 *
 * Single page per country at /smswiser/:countryKey
 * - Stats: select campaigns → tabs for each
 * - Prepared drops: select campaign → creator + drafts
 */

import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Navigate, useParams, useSearchParams } from 'react-router-dom';
import { motion, useReducedMotion } from 'framer-motion';

import { Card } from '@/components/ui';
import { useAuth, useCountry } from '@/hooks';
import {
  useMySmswiserCampaignPins,
  usePinSmswiserCampaign,
  useUnpinSmswiserCampaign,
} from '@/hooks';
import {
  useCountryCreative,
  useUpdateCountryTitlePrefix,
} from '@/hooks';
import { todayIsoDateParis } from '@/features/smswiser-campaigns/utils/dateFormat';

import { getDefaultPreparedCampaignId } from './constants';
import { useSmswiserPreparedDeepLink } from './hooks/useSmswiserPreparedDeepLink';
import { PreparedSection } from './sections/PreparedSection';
import { SmswiserCampaignDetailHeader } from './header/SmswiserCampaignDetailHeader';
import { StatsSection } from './sections/StatsSection';
import { TrafficSourceDialog, trafficCampaignGroupKey } from './sections/stats-traffic';
import { buildTabGroups, useTrafficLinkRules, type TrafficDialogState } from './tracker';
import type { MainTab, PreparedSubTab } from './types';

export const SmswiserCampaignDetailPage: FC = () => {
  const prefersReducedMotion = useReducedMotion();
  const { hasPermission } = useAuth();
  const statsOnlyUser = hasPermission('stats.phone') && !hasPermission('drops.view');
  const params = useParams();

  const countryKey = params.countryKey || '';
  const hasCountryKey = Boolean(countryKey);
  const prevCountryKeyRef = useRef<string>(countryKey);

  const [searchParams, setSearchParams] = useSearchParams();
  const viewParam = searchParams.get('view') as MainTab | null;
  const mainTab: MainTab = viewParam === 'prepared' ? 'prepared' : 'stats';
  const setMainTab = useCallback(
    (tab: MainTab) => setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (tab === 'stats') next.delete('view');
      else next.set('view', tab);
      return next;
    }, { replace: true }),
    [setSearchParams],
  );

  const [preparedSubTab, setPreparedSubTab] = useState<PreparedSubTab>('drafts');
  // ?date=YYYY-MM-DD deep-links a specific day (the Drop Generator's
  // "Open today's drafts" uses it — the run day is recipient-local).
  const [workDate, setWorkDate] = useState<string>(() => {
    const fromUrl = searchParams.get('date');
    return fromUrl && /^\d{4}-\d{2}-\d{2}$/.test(fromUrl)
      ? fromUrl
      : todayIsoDateParis();
  });

  const [statsCampaignIds, setStatsCampaignIds] = useState<number[]>([]);
  const [activeStatsTabIndex, setActiveStatsTabIndex] = useState(0);
  const [preparedCampaignId, setPreparedCampaignId] = useState<number | null>(null);
  const [trafficDialog, setTrafficDialog] = useState<TrafficDialogState | null>(null);
  const linkRules = useTrafficLinkRules();

  const { highlightPreparedDropId, clearFocusPreparedDropParam } = useSmswiserPreparedDeepLink({
    searchParams,
    setSearchParams,
    setPreparedCampaignId,
    setWorkDate,
    setPreparedSubTab,
  });

  const [titlePrefixInput, setTitlePrefixInput] = useState('');
  const [prefixStatus, setPrefixStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const prevServerPrefixRef = useRef<string | null | undefined>(undefined);
  const hasAppliedPinnedRef = useRef(false);

  const country = useCountry(countryKey);
  const displayCountryKey =
    country.isFetching && country.data ? prevCountryKeyRef.current || countryKey : countryKey;
  if (!country.isFetching) prevCountryKeyRef.current = countryKey;

  const campaigns = country.data?.campaigns ?? [];
  const countryName = country.data?.countryName ?? countryKey;

  const pinnedCampaigns = useMySmswiserCampaignPins();
  const pinCampaign = usePinSmswiserCampaign();
  const unpinCampaign = useUnpinSmswiserCampaign();

  const countryCreative = useCountryCreative(countryKey, { enabled: !statsOnlyUser });
  const updateTitlePrefix = useUpdateCountryTitlePrefix(countryKey);

  const serverTitlePrefix = countryCreative.data?.titlePrefix ?? null;

  useEffect(() => {
    if (prevServerPrefixRef.current !== serverTitlePrefix) {
      prevServerPrefixRef.current = serverTitlePrefix;
      setTitlePrefixInput(serverTitlePrefix ?? '');
      setPrefixStatus('idle');
    }
  }, [serverTitlePrefix]);

  useEffect(() => {
    if (!countryKey || !preparedCampaignId) return;
    if (countryCreative.isLoading) return;
    const trimmed = titlePrefixInput.trim();
    const value = trimmed || null;
    if (value === serverTitlePrefix) return;

    setPrefixStatus('saving');
    const t = window.setTimeout(() => {
      updateTitlePrefix.mutate(value, {
        onSuccess: () => setPrefixStatus('saved'),
        onError: () => setPrefixStatus('error'),
      });
    }, 450);
    return () => window.clearTimeout(t);
  }, [countryCreative.isLoading, countryKey, preparedCampaignId, serverTitlePrefix, titlePrefixInput, updateTitlePrefix]);

  const pinnedWiserIdsForCountry = useMemo(
    () =>
      (pinnedCampaigns.data ?? [])
        .filter((p) => p.countryKey.toLowerCase() === displayCountryKey.toLowerCase())
        .map((p) => p.wiserId),
    [pinnedCampaigns.data, displayCountryKey],
  );

  useEffect(() => {
    hasAppliedPinnedRef.current = false;
  }, [displayCountryKey]);

  useEffect(() => {
    if (mainTab === 'prepared' && campaigns.length > 0 && preparedCampaignId == null) {
      const id = getDefaultPreparedCampaignId(campaigns);
      if (id != null) setPreparedCampaignId(id);
    }
  }, [mainTab, campaigns, preparedCampaignId]);

  useEffect(() => {
    if (!hasAppliedPinnedRef.current && pinnedWiserIdsForCountry.length > 0 && country.data) {
      hasAppliedPinnedRef.current = true;
      setStatsCampaignIds((prev) => {
        const merged = new Set([...prev, ...pinnedWiserIdsForCountry]);
        return Array.from(merged);
      });
    }
  }, [pinnedWiserIdsForCountry, country.data]);

  const handlePinGroup = useCallback(
    (wiserIds: number[]) => {
      wiserIds.forEach((wid) => pinCampaign.mutate({ countryKey, wiserId: wid }));
    },
    [countryKey, pinCampaign],
  );

  const handleUnpinGroup = useCallback(
    (wiserIds: number[]) => {
      wiserIds.forEach((wid) => unpinCampaign.mutate({ countryKey, wiserId: wid }));
    },
    [countryKey, unpinCampaign],
  );

  /** Opens tabs without the dialog — for links it cannot show (Tres Commas where disabled). */
  const showStatsTabs = useCallback((ids: number[]) => {
    setStatsCampaignIds((prev) => Array.from(new Set([...prev, ...ids])));
    setActiveStatsTabIndex(999);
  }, []);

  const addStatsCampaign = useCallback((id: number) => {
    const campaign = campaigns.find((c) => c.id === id);
    if (!campaign) return;
    const gk = trafficCampaignGroupKey(campaign);
    const sameGroup = campaigns.filter((c) => trafficCampaignGroupKey(c) === gk);
    if (!linkRules.campaignEditable(campaign)) {
      showStatsTabs(sameGroup.map((c) => c.id));
      return;
    }
    setTrafficDialog({ wiserIds: sameGroup.map((c) => c.id), mode: 'add' });
  }, [campaigns, linkRules, showStatsTabs]);

  const handleTrafficSaved = useCallback(() => {
    setTrafficDialog((current) => {
      if (current?.mode === 'add') {
        const ids = current.wiserIds;
        setStatsCampaignIds((prev) => Array.from(new Set([...prev, ...ids])));
        setActiveStatsTabIndex(999);
      }
      return null;
    });
  }, []);

  const campaignsForStats = useMemo(
    () =>
      statsCampaignIds
        .map((id) => campaigns.find((c) => c.id === id))
        .filter((c): c is NonNullable<typeof c> => !!c),
    [campaigns, statsCampaignIds],
  );

  const tabGroups = useMemo(() => buildTabGroups(campaignsForStats), [campaignsForStats]);

  const handleEditTrafficLink = useCallback(() => {
    const g = tabGroups[activeStatsTabIndex];
    if (!g) return;
    setTrafficDialog({ wiserIds: g.wiserIds, mode: 'edit' });
  }, [tabGroups, activeStatsTabIndex]);

  const trafficDialogPayload = useMemo(
    () => linkRules.payloadFor(trafficDialog, tabGroups, campaigns),
    [linkRules, trafficDialog, tabGroups, campaigns],
  );

  useEffect(() => {
    if (trafficDialog && !trafficDialogPayload) setTrafficDialog(null);
  }, [trafficDialog, trafficDialogPayload]);

  const removeStatsTabGroup = useCallback(
    (tabIndex: number) => {
      const group = tabGroups[tabIndex];
      if (!group) return;
      setStatsCampaignIds((prev) => prev.filter((id) => !group.wiserIds.includes(id)));
      setActiveStatsTabIndex((idx) => {
        if (idx === tabIndex) return Math.max(0, tabIndex - 1);
        if (idx > tabIndex) return idx - 1;
        return idx;
      });
    },
    [tabGroups],
  );

  useEffect(() => {
    setActiveStatsTabIndex((prev) =>
      prev >= tabGroups.length ? Math.max(0, tabGroups.length - 1) : prev,
    );
  }, [tabGroups.length]);

  const handleMainTabChange = useCallback((tab: MainTab) => {
    if (tab === 'prepared') setPreparedSubTab('drafts');
    setMainTab(tab);
  }, [setMainTab]);

  useEffect(() => {
    if (statsOnlyUser && mainTab === 'prepared') {
      setMainTab('stats');
    }
  }, [statsOnlyUser, mainTab, setMainTab]);

  if (!hasCountryKey) {
    return <Navigate to="/smswiser" replace />;
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-6">
      <SmswiserCampaignDetailHeader
        displayCountryKey={displayCountryKey}
        countryKey={countryKey}
        countryName={countryName}
        isFetching={country.isFetching}
        mainTab={mainTab}
        workDate={workDate}
        onWorkDateChange={setWorkDate}
        onMainTabChange={handleMainTabChange}
        hidePreparedTab={statsOnlyUser}
        showTitlePrefix={mainTab === 'prepared' && preparedCampaignId != null}
        titlePrefixInput={titlePrefixInput}
        onTitlePrefixChange={setTitlePrefixInput}
        prefixStatus={prefixStatus}
      />

      <Card className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden p-4">
        <motion.div
          key={mainTab}
          initial={prefersReducedMotion ? false : { opacity: 0, y: 8 }}
          animate={prefersReducedMotion ? { opacity: 1 } : { opacity: 1, y: 0 }}
          exit={prefersReducedMotion ? undefined : { opacity: 0, y: -8 }}
          className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden"
        >
          {mainTab === 'stats' || statsOnlyUser ? (
            <StatsSection
              countryKey={displayCountryKey}
              campaigns={campaigns}
              tabGroups={tabGroups}
              activeStatsTabIndex={activeStatsTabIndex}
              workDate={workDate}
              serverTitlePrefix={serverTitlePrefix ?? ''}
              onAddCampaign={addStatsCampaign}
              onEditTrafficLink={
                linkRules.groupEditable(tabGroups[activeStatsTabIndex]) ? handleEditTrafficLink : undefined
              }
              onRemoveTabGroup={removeStatsTabGroup}
              onTabChange={setActiveStatsTabIndex}
              onPinGroup={handlePinGroup}
              onUnpinGroup={handleUnpinGroup}
              pinnedWiserIds={pinnedWiserIdsForCountry}
              isLoading={!country.data && country.isFetching}
            />
          ) : !statsOnlyUser ? (
            <PreparedSection
              countryKey={displayCountryKey}
              campaigns={campaigns}
              selectedCampaignId={preparedCampaignId}
              workDate={workDate}
              serverTitlePrefix={serverTitlePrefix ?? ''}
              defaultSubTab={preparedSubTab}
              onSelectCampaign={setPreparedCampaignId}
              isLoading={!country.data && country.isFetching}
              highlightPreparedDropId={highlightPreparedDropId}
              onInvestigationFocusConsumed={clearFocusPreparedDropParam}
            />
          ) : null}
        </motion.div>
      </Card>

      {trafficDialogPayload ? (
        <TrafficSourceDialog
          open
          countryKey={displayCountryKey}
          wiserIds={trafficDialogPayload.wiserIds}
          initialProvider={trafficDialogPayload.initialProvider}
          initialTrafficCampaignId={trafficDialogPayload.initialTrafficCampaignId}
          onOpenChange={(o) => {
            if (!o) setTrafficDialog(null);
          }}
          onSaved={handleTrafficSaved}
        />
      ) : null}
    </div>
  );
};
